import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LEGAL_TEXT_TYPES,
  uploadLegalText,
  deleteLegalText,
} from "@/lib/legal";
import { formatBytes } from "@/lib/documents";

const MAX_MB = 100;

export default function LegalCorpusManager({
  texts,
  selectedIds,
  onToggle,
  onUploaded,
  onDeleted,
}) {
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [title, setTitle] = useState("");
  const [textType, setTextType] = useState(LEGAL_TEXT_TYPES[0]);
  const [jurisdiction, setJurisdiction] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const resetForm = () => {
    setPendingFile(null);
    setTitle("");
    setJurisdiction("");
    setTextType(LEGAL_TEXT_TYPES[0]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les PDF sont acceptés.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (> ${MAX_MB} Mo).`);
      return;
    }
    setPendingFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ""));
  };

  const handleUpload = async () => {
    if (!pendingFile || !title.trim()) {
      toast.error("Titre et fichier requis.");
      return;
    }
    setUploading(true);
    try {
      const created = await uploadLegalText({
        file: pendingFile,
        title: title.trim(),
        textType,
        jurisdiction: jurisdiction.trim() || null,
      });
      toast.success("Texte juridique ajouté à votre corpus.");
      resetForm();
      onUploaded?.(created);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Impossible d'ajouter ce texte juridique."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce texte juridique ?")) return;
    setDeletingId(id);
    try {
      await deleteLegalText(id);
      toast.success("Texte juridique supprimé.");
      onDeleted?.(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card data-testid="legal-corpus-manager">
      <CardHeader>
        <CardTitle className="text-base">Corpus juridique de référence</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Upload form */}
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="legal-title">Titre du texte</Label>
              <Input
                id="legal-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Code pénal — Livre III"
                data-testid="legal-upload-title"
                maxLength={200}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={textType} onValueChange={setTextType}>
                <SelectTrigger data-testid="legal-upload-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEGAL_TEXT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`legal-type-opt-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="legal-jurisdiction">Juridiction (optionnel)</Label>
              <Input
                id="legal-jurisdiction"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
                placeholder="Sénégal, France, OHADA…"
                data-testid="legal-upload-jurisdiction"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="legal-file">Fichier PDF</Label>
              <Input
                id="legal-file"
                ref={fileRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                data-testid="legal-upload-file"
              />
              {pendingFile && (
                <div className="text-xs text-muted-foreground">
                  {pendingFile.name} · {formatBytes(pendingFile.size)}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={uploading || !pendingFile || !title.trim()}
              data-testid="legal-upload-submit"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Envoi…
                </>
              ) : (
                <>
                  <UploadCloud className="mr-2 h-4 w-4" />
                  Ajouter au corpus
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Corpus list */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold">
              Ma bibliothèque&nbsp;
              <span className="text-muted-foreground">({texts.length})</span>
            </h4>
            <div className="text-xs text-muted-foreground">
              Cochez les textes à inclure dans l'analyse
            </div>
          </div>

          {texts.length === 0 ? (
            <div
              className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground"
              data-testid="legal-corpus-empty"
            >
              Aucun texte juridique dans votre corpus. Ajoutez au moins un PDF
              (Code pénal, loi anticorruption, etc.) pour lancer l'analyse.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="legal-corpus-list">
              {texts.map((t) => {
                const checked = selectedIds.includes(t.id);
                return (
                  <li
                    key={t.id}
                    className={`rounded-md border p-3 transition-colors ${
                      checked
                        ? "border-brand-gold/60 bg-brand-gold/5"
                        : "border-border bg-card"
                    }`}
                    data-testid={`legal-corpus-row-${t.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <label
                        htmlFor={`legal-check-${t.id}`}
                        className="flex flex-1 cursor-pointer items-center gap-3"
                      >
                        <Checkbox
                          id={`legal-check-${t.id}`}
                          checked={checked}
                          onCheckedChange={() => onToggle(t.id)}
                          data-testid={`legal-corpus-check-${t.id}`}
                          aria-label={`Inclure ${t.title}`}
                        />
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold">
                            {t.title}
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {t.text_type}
                            {t.jurisdiction && ` · ${t.jurisdiction}`}
                            {t.page_count != null && ` · ${t.page_count} p.`}
                            {t.article_count != null &&
                              ` · ${t.article_count} articles détectés`}
                            {t.size_bytes != null && ` · ${formatBytes(t.size_bytes)}`}
                          </div>
                        </div>
                      </label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(t.id)}
                        disabled={deletingId === t.id}
                        data-testid={`legal-corpus-delete-${t.id}`}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        {deletingId === t.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
